/**
 * VoiceFillModal — the headline AI flow.
 *
 * Caregiver opens this from the top of ShiftReport, records a 1-5 min audio
 * monologue, and gets back a structured extraction (vitals, mood, sleep,
 * fluid, daily activity, checklist, shift notes). User reviews each section,
 * toggles which to apply, and submits.
 *
 * The modal NEVER writes data on its own — it only calls back into the
 * parent ShiftReport via `onApply(payload)` so the parent decides which
 * fields to merge into its state.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Mic, Square, Loader2, Sparkles, X, Check, AlertTriangle,
  Activity, Smile, Moon, Droplets, ListChecks, ClipboardList, FileText,
} from "lucide-react";

const RECORD_LIMIT_MS = 5 * 60 * 1000; // 5 minutes

const SECTIONS = [
  { k: "shift", l: "Shift notes", icon: FileText, accent: "#0F172A" },
  { k: "vitals", l: "Vitals", icon: Activity, accent: "#EF4444" },
  { k: "checklist", l: "Care checklist", icon: ListChecks, accent: "#16A34A" },
  { k: "mood", l: "Mood", icon: Smile, accent: "#F59E0B" },
  { k: "sleep", l: "Sleep", icon: Moon, accent: "#7C3AED" },
  { k: "fluid", l: "Fluid intake", icon: Droplets, accent: "#0EA5E9" },
  { k: "daily_activity", l: "Daily activity", icon: ClipboardList, accent: "#16A34A" },
];

function ConfBadge({ value }) {
  if (value == null) return null;
  const v = Number(value) || 0;
  const tone = v >= 0.8 ? "#16A34A" : v >= 0.5 ? "#F59E0B" : "#EF4444";
  return (
    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: tone + "1a", color: tone }}>
      {Math.round(v * 100)}%
    </span>
  );
}

function sectionEmpty(k, payload) {
  if (!payload) return true;
  const v = payload[k];
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.values(v).every((x) => x == null || x === "");
  return false;
}

function VitalsPreview({ v }) {
  const rows = [
    ["BP", v.systolic && v.diastolic ? `${v.systolic}/${v.diastolic}` : null],
    ["Pulse", v.pulse],
    ["Temp", v.temp_f != null ? `${v.temp_f}°F` : null],
    ["O₂", v.o2_sat != null ? `${v.o2_sat}%` : null],
    ["Weight", v.weight_lb != null ? `${v.weight_lb} lb` : null],
    ["Glucose", v.blood_sugar],
  ].filter(([, val]) => val != null);
  if (rows.length === 0) return <p className="text-xs text-clinical-subtext">Nothing detected.</p>;
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      {rows.map(([k, val]) => (
        <div key={k} className="rounded-md bg-white p-1.5 text-center">
          <div className="text-[10px] uppercase text-clinical-subtext">{k}</div>
          <div className="font-semibold text-clinical-text">{val}</div>
        </div>
      ))}
    </div>
  );
}

function ChecklistPreview({ c }) {
  const set = Object.entries(c || {}).filter(([, v]) => v != null);
  if (set.length === 0) return <p className="text-xs text-clinical-subtext">No items detected.</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {set.map(([k, v]) => (
        <span key={k}
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            v ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600 line-through"
          }`}>
          {k}
        </span>
      ))}
    </div>
  );
}

function MoodPreview({ m }) {
  if (m.rating == null) return <p className="text-xs text-clinical-subtext">No mood detected.</p>;
  const label = { "-2": "Withdrawn", "-1": "Low", "0": "Calm",
    "1": "Restless", "2": "Agitated" }[String(m.rating)] || `Rating ${m.rating}`;
  return <span className="text-sm font-medium text-clinical-text">{label}{m.shift ? ` · ${m.shift}` : ""}</span>;
}

function SleepPreview({ s }) {
  if (s.quality == null && s.total_hours == null && !s.position_notes) {
    return <p className="text-xs text-clinical-subtext">No sleep info detected.</p>;
  }
  return (
    <div className="text-sm text-clinical-text">
      {s.total_hours != null && <span><b>{s.total_hours}h</b> </span>}
      {s.quality && <span>· {s.quality}</span>}
      {s.position_notes && <div className="text-xs text-clinical-subtext mt-1">{s.position_notes}</div>}
    </div>
  );
}

function FluidPreview({ entries }) {
  if (!entries || entries.length === 0) {
    return <p className="text-xs text-clinical-subtext">No fluid entries detected.</p>;
  }
  return (
    <ul className="space-y-1 text-sm">
      {entries.map((e, i) => (
        <li key={i} className="flex items-center justify-between rounded-md bg-white px-2 py-1">
          <span className="capitalize">{e.fluid_type || "drink"}</span>
          <span className="text-clinical-subtext">
            {e.refused ? "refused" : (e.amount_ml != null ? `${e.amount_ml} ml` : "—")}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DailyActivityPreview({ d }) {
  const adls = ["bathing", "dressing", "grooming", "toileting", "eating"]
    .map((k) => [k, d[k]]).filter(([, v]) => v);
  const hasAny = adls.length > 0 || d.social_engagement || d.appetite || (d.activities || []).length > 0;
  if (!hasAny) return <p className="text-xs text-clinical-subtext">Nothing detected.</p>;
  return (
    <div className="space-y-2 text-sm">
      {adls.length > 0 && (
        <div className="grid grid-cols-2 gap-1">
          {adls.map(([k, v]) => (
            <div key={k} className="flex justify-between rounded bg-white px-2 py-1">
              <span className="capitalize">{k}</span>
              <span className="text-clinical-subtext">{v.replace(/_/g, " ")}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 text-xs text-clinical-subtext">
        {d.appetite && <span>Appetite: <b>{d.appetite}</b></span>}
        {d.social_engagement && <span>Social: <b>{d.social_engagement}</b></span>}
      </div>
      {(d.activities || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {d.activities.map((a) => (
            <span key={a} className="rounded-full bg-clinical-bg px-2 py-0.5 text-[11px]">{a}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function ShiftPreview({ s }) {
  if (!s.raw_notes && !s.formatted_notes) {
    return <p className="text-xs text-clinical-subtext">No notes detected.</p>;
  }
  return (
    <div className="space-y-2">
      <div>
        <div className="label-eyebrow mb-1">Raw notes</div>
        <p className="text-sm text-clinical-text whitespace-pre-line">{s.raw_notes || "—"}</p>
      </div>
      {s.formatted_notes && (
        <div>
          <div className="label-eyebrow mb-1">Clinical formatting (preview)</div>
          <p className="text-sm text-clinical-text whitespace-pre-line">{s.formatted_notes}</p>
        </div>
      )}
      {s.shift_type && (
        <div className="text-xs text-clinical-subtext">Shift: <b>{s.shift_type}</b></div>
      )}
      {s.incident_flag && (
        <div className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
          <AlertTriangle size={12} /> Incident flagged — review WellnessPanel / CriticalIncident
        </div>
      )}
    </div>
  );
}

const PREVIEW = {
  shift: ShiftPreview, vitals: VitalsPreview, checklist: ChecklistPreview,
  mood: MoodPreview, sleep: SleepPreview, fluid: FluidPreview,
  daily_activity: DailyActivityPreview,
};

export default function VoiceFillModal({ open, onClose, clientId, onApply }) {
  // recording
  const [state, setState] = useState("idle"); // idle|recording|processing|done
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});  // sectionKey -> bool

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const startTimeRef = useRef(0);
  const limitTimerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      clearInterval(tickRef.current);
      clearTimeout(limitTimerRef.current);
    };
  }, [open]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setState("idle"); setElapsed(0); setResult(null); setSelected({});
    }
  }, [open]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mr.start();
      startTimeRef.current = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => {
        setElapsed(Date.now() - startTimeRef.current);
      }, 200);
      limitTimerRef.current = setTimeout(() => {
        if (mediaRef.current && mediaRef.current.state === "recording") {
          mediaRef.current.stop();
          toast.info("Reached 5-minute limit — stopping & processing");
        }
      }, RECORD_LIMIT_MS);
      setState("recording");
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current?.state === "recording") {
      mediaRef.current.stop();
    }
  };

  const handleStop = async () => {
    clearInterval(tickRef.current);
    clearTimeout(limitTimerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    // Use the recorder's actual mimeType so the multipart upload reflects the
    // browser's chosen codec (Safari may fall back to mp4/aac).
    const recordedType = mediaRef.current?.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: recordedType });
    if (blob.size === 0) { setState("idle"); return; }
    setState("processing");
    try {
      const fd = new FormData();
      // Pick file extension from MIME for Whisper compatibility
      const ext = recordedType.includes("mp4") ? "mp4"
                : recordedType.includes("ogg") ? "ogg"
                : recordedType.includes("wav") ? "wav"
                : "webm";
      fd.append("audio", blob, `shift.${ext}`);
      if (clientId) fd.append("client_id", clientId);
      const { data } = await api.post("/ai/voice-fill/shift", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(data);
      // Pre-select every non-empty section by default
      const sel = {};
      SECTIONS.forEach((s) => { sel[s.k] = !sectionEmpty(s.k, data); });
      setSelected(sel);
      setState("done");
      const hasSpeech = (data.transcript || "").length > 5;
      if (!hasSpeech) {
        toast.error("No speech detected — try recording again");
      } else if ((data.warnings || []).length === 0) {
        toast.success("Extracted shift data — review and apply");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Voice fill failed — try again";
      toast.error(msg);
      setState("idle");
    }
  };

  const apply = () => {
    if (!result) return;
    const picked = {};
    SECTIONS.forEach((s) => { if (selected[s.k]) picked[s.k] = result[s.k]; });
    picked._transcript = result.transcript;
    onApply?.(picked);
    onClose?.();
    toast.success("Voice data applied — review and submit");
  };

  if (!open) return null;

  const seconds = Math.floor(elapsed / 1000);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="voice-fill-modal">
      <div className="card-surface w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#7C3AED1a", color: "#7C3AED" }}>
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">AI Voice Fill</h2>
              <p className="text-xs text-clinical-subtext">
                {state === "idle" && "Tap the mic, describe your shift naturally, tap stop."}
                {state === "recording" && `Recording · ${mm}:${ss}`}
                {state === "processing" && "Transcribing + extracting…"}
                {state === "done" && "Review and pick what to apply"}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close" data-testid="voice-fill-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {state !== "done" && (
            <div className="flex flex-col items-center gap-4 py-6">
              {state === "idle" && (
                <p className="text-sm text-center text-clinical-subtext max-w-md">
                  Example: <i>"Margaret had a quiet morning. BP 138 over 82, pulse 74,
                  temp 98.2. Ate half her oatmeal, drank 8oz of juice. Refused her
                  coffee. Walked to the bathroom with her walker. Slept 6 hours."</i>
                </p>
              )}
              {state === "recording" && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-24 w-24">
                    <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
                    <div className="relative h-24 w-24 rounded-full bg-red-500 flex items-center justify-center">
                      <Mic className="h-10 w-10 text-white" />
                    </div>
                  </div>
                  <div className="text-2xl font-mono tabular-nums text-clinical-text" data-testid="voice-fill-timer">
                    {mm}:{ss}
                  </div>
                </div>
              )}
              {state === "processing" && (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />
                  <p className="text-sm text-clinical-subtext">
                    Whisper → Claude Sonnet — usually 5-15 seconds…
                  </p>
                </>
              )}
              <button
                type="button"
                onClick={state === "recording" ? stopRecording : startRecording}
                disabled={state === "processing"}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  state === "recording"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-clinical-accent text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={state === "recording" ? "voice-fill-stop" : "voice-fill-start"}
              >
                {state === "recording" ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {state === "recording" ? "Stop & process" : state === "processing" ? "Processing…" : "Start recording"}
              </button>
            </div>
          )}

          {state === "done" && result && (
            <div className="space-y-3" data-testid="voice-fill-results">
              {/* Empty transcript — show clear empty state */}
              {(!result.transcript || result.transcript.length < 5) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                  <AlertTriangle className="mx-auto h-6 w-6 text-amber-600" />
                  <p className="mt-2 text-sm font-medium text-amber-900">No speech detected</p>
                  <p className="mt-1 text-xs text-amber-800">
                    Try recording again in a quieter spot, speak closer to the mic, or hold the
                    device closer to your mouth.
                  </p>
                </div>
              )}
              {/* Transcript */}
              {result.transcript && (
                <details className="card-surface p-3 bg-clinical-bg/40">
                  <summary className="cursor-pointer text-xs font-semibold uppercase text-clinical-subtext">
                    Transcript
                  </summary>
                  <p className="mt-2 text-sm text-clinical-text whitespace-pre-line">{result.transcript}</p>
                </details>
              )}

              {/* Warnings */}
              {(result.warnings || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={12} /> AI warnings
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Per-section toggles */}
              {SECTIONS.map((s) => {
                const empty = sectionEmpty(s.k, result);
                const PreviewC = PREVIEW[s.k];
                return (
                  <div key={s.k} className={`card-surface p-3 transition ${empty ? "opacity-40" : ""}`}
                    data-testid={`voice-fill-section-${s.k}`}>
                    <label className="flex items-center justify-between gap-2 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!selected[s.k] && !empty}
                          disabled={empty}
                          onChange={(e) => setSelected({ ...selected, [s.k]: e.target.checked })}
                          className="h-4 w-4 rounded text-clinical-accent"
                          data-testid={`voice-fill-toggle-${s.k}`}
                        />
                        <s.icon size={14} style={{ color: s.accent }} />
                        <span className="font-medium text-sm text-clinical-text">{s.l}</span>
                        <ConfBadge value={result.confidence?.[s.k]} />
                      </div>
                    </label>
                    <div className="mt-2 pl-6">
                      <PreviewC {...(s.k === "fluid" ? { entries: result.fluid }
                        : s.k === "shift" ? { s: result.shift }
                        : s.k === "vitals" ? { v: result.vitals }
                        : s.k === "checklist" ? { c: result.checklist }
                        : s.k === "mood" ? { m: result.mood }
                        : s.k === "sleep" ? { s: result.sleep }
                        : { d: result.daily_activity })} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {state === "done" && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between gap-2">
            <button type="button" onClick={() => { setResult(null); setState("idle"); }}
              className="text-sm text-clinical-subtext hover:text-clinical-text"
              data-testid="voice-fill-redo">
              Record again
            </button>
            <button type="button" onClick={apply} className="btn-primary"
              data-testid="voice-fill-apply">
              <Check size={14} /> Apply to form
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
